<script setup>
import AutocompleteFilter from '@/components/widgets/AutocompleteFilter.vue';
import { ref, inject, onMounted, watch, nextTick } from 'vue';

const backendApi = inject('backendApi');

const emit = defineEmits(['update']);
const props = defineProps({
    hideLabel: {
        type: Boolean,
        default: false
    },
    itemTitle: {
        default: 'name'
    },
    itemValue: {
        default: 'id'
    },
    args: {
        type: Object,
        default: () => ({})
    }
});

const selected = ref(null);
const searchQuery = ref('');
const loading = ref(true);
const regions = ref([]);
const showRetryButton = ref(false);

const getRegions = () => {
    cleanData();

    let country = null;
    if (props.args?.country) {
        country = props.args?.country;
    }

    nextTick(() => {
        backendApi()
            .regions()
            .index({
                pattern: searchQuery.value,
                active: true,
                country: country?.id ?? country
            })
            .then(success, fail)
            .finally(() => (loading.value = false));

        function success(response) {
            regions.value = response.data;
        }

        function fail() {
            showRetryButton.value = true;
        }
    });
};

const cleanData = () => {
    regions.value = [];
    loading.value = true;
    showRetryButton.value = false;
};

watch(
    () => props.args?.country,
    () => {
        getRegions();
    },
    { immediate: true }
);

onMounted(() => getRegions());
</script>

<template>
    <AutocompleteFilter
        v-model="selected"
        v-model:search="searchQuery"
        :item-title="itemTitle"
        :item-value="itemValue"
        :items="regions"
        :label="hideLabel ? '' : $t('Regions')"
        :loading="loading"
        :show-retry-button="showRetryButton"
        @update="emit('update', selected)"
        @preparing-search="cleanData"
        @search="getRegions"
        @retry="getRegions"
    >
        <template v-slot:default="{ props, item }">
            <v-list-item
                v-bind="props"
                :title="`${item.raw.name}`"
                :subtitle="`${item.raw.country?.name}`"
            >
            </v-list-item>
        </template>

        <template v-if="$slots.prepend" v-slot:prepend>
            <slot name="prepend"></slot>
        </template>
    </AutocompleteFilter>
</template>
